import axios from 'axios'

const apiURL = process.env.REACT_APP_API_URL

export interface NotifiableWorkDocument {
  notifiableWorkDocumentId: number
  notifiableWorkId: number
  name: string
  /** Counts towards the notifiable work's badge on a job. */
  isRequired: boolean
  /** Done by uploading a file; otherwise ticked off on the job with a switch. */
  isFileUpload: boolean
}

/** Only what is sent changes. */
export interface NotifiableWorkDocumentChanges {
  name?: string
  isRequired?: boolean
  isFileUpload?: boolean
}

export interface NotifiableWorkAdmin {
  notifiableWorkId: number
  name: string
  /** How many jobs carry it. */
  jobCount: number
  documents: NotifiableWorkDocument[]
}

export interface ServiceTypeAdmin {
  serviceTypeId: number
  name: string
  /** How many service reminders carry it. */
  reminderCount: number
}

const headers = () => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  return {
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + token,
  }
}

const url = (path: string) => `https://${apiURL}/${path}`

// --- Notifiable works --------------------------------------------------------

export const getNotifiableWorksForAdmin = async (): Promise<NotifiableWorkAdmin[]> =>
  (await axios.get(url('NotifiableWork/Manage'), { headers: headers() })).data

export const createNotifiableWork = async (name: string): Promise<NotifiableWorkAdmin> =>
  (await axios.post(url('NotifiableWork'), { name }, { headers: headers() })).data

/** Also renames it on every job that carries it. */
export const renameNotifiableWork = async (id: number, name: string): Promise<NotifiableWorkAdmin> =>
  (await axios.put(url(`NotifiableWork/${id}`), { name }, { headers: headers() })).data

/** Retires it: no longer offered on jobs, but jobs that carry it keep it. */
export const deleteNotifiableWork = async (id: number): Promise<void> => {
  await axios.delete(url(`NotifiableWork/${id}`), { headers: headers() })
}

export const addNotifiableWorkDocument = async (notifiableWorkId: number, name: string): Promise<NotifiableWorkDocument> =>
  (await axios.post(url(`NotifiableWork/${notifiableWorkId}/Documents`), { name }, { headers: headers() })).data

export const updateNotifiableWorkDocument = async (
  documentId: number,
  changes: NotifiableWorkDocumentChanges
): Promise<NotifiableWorkDocument> =>
  (await axios.put(url(`NotifiableWork/Documents/${documentId}`), changes, { headers: headers() })).data

export const renameNotifiableWorkDocument = (documentId: number, name: string) =>
  updateNotifiableWorkDocument(documentId, { name })

/** Every one of the work's document ids, in the order they should be listed. */
export const reorderNotifiableWorkDocuments = async (notifiableWorkId: number, documentIds: number[]): Promise<NotifiableWorkDocument[]> =>
  (await axios.put(url(`NotifiableWork/${notifiableWorkId}/Documents/Order`), documentIds, { headers: headers() })).data

export const deleteNotifiableWorkDocument = async (documentId: number): Promise<void> => {
  await axios.delete(url(`NotifiableWork/Documents/${documentId}`), { headers: headers() })
}

// --- Service types -----------------------------------------------------------

export const getServiceTypesForAdmin = async (): Promise<ServiceTypeAdmin[]> =>
  (await axios.get(url('ServiceType/Manage'), { headers: headers() })).data

export const createServiceType = async (name: string): Promise<ServiceTypeAdmin> =>
  (await axios.post(url('ServiceType'), { name }, { headers: headers() })).data

export const renameServiceType = async (id: number, name: string): Promise<ServiceTypeAdmin> =>
  (await axios.put(url(`ServiceType/${id}`), { name }, { headers: headers() })).data

/** Retires it: no longer offered on reminders, but reminders that carry it keep it. */
export const deleteServiceType = async (id: number): Promise<void> => {
  await axios.delete(url(`ServiceType/${id}`), { headers: headers() })
}
